import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const AddressManager = () => {
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    house: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    lat: null,
    lng: null,
    address: "",
    isDefault: false,
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);

  // 📍 LOCATION
  const getLocation = () => {
    if (!navigator.geolocation) {
      alert("❌ Location not supported on your device");
      return;
    }

    setLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude);
        const lng = Number(pos.coords.longitude);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await res.json();
          const addr = data.address || {};

          const fullAddress = `${addr.road || ""}, ${addr.suburb || addr.village || ""}, ${addr.city || addr.town || ""}, ${addr.state || ""} - ${addr.postcode || ""}`;

          setForm((prev) => ({
            ...prev,
            house: addr.road || "",
            area: addr.suburb || addr.village || "",
            city: addr.city || addr.town || "",
            state: addr.state || "",
            pincode: addr.postcode || "",
            lat,
            lng,
            address: fullAddress,
          }));
        } catch {
          alert("❌ Failed to fetch location address");
        }

        setLoadingLocation(false);
      },
      (error) => {
        if (error.code === 1) {
          alert("⚠️ Location permission denied. Please enable it.");
        } else {
          alert("⚠️ Please turn ON your location (GPS)");
        }
        setLoadingLocation(false);
      }
    );
  };

  // INPUT HANDLE
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let finalValue = value;

    if (name === "phone") finalValue = value.replace(/\D/g, "");
    if (name === "pincode") finalValue = value.replace(/\D/g, "");

    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : finalValue,
    });
  };

  // VALIDATION
  const validate = () => {
    if (!form.name.trim()) return alert("Name required");

    if (!form.phone || form.phone.length < 10)
      return alert("Valid phone required");

    if (!form.lat || !form.lng)
      return alert("📍 Please click 'Use Current Location' first");

    if (!form.house && !form.area) return alert("Enter address");

    if (!form.city || !form.state)
      return alert("City & state required");

    if (!form.pincode || form.pincode.length < 6)
      return alert("Valid pincode required");

    return true;
  };

  // SAVE + 🔥 REDIRECT
  const saveAddress = () => {
    if (!validate()) return;

    const fullAddress = `${form.house}, ${form.area}, ${form.city}, ${form.state} - ${form.pincode}`;

    const newAddress = {
      name: String(form.name),
      phone: Number(form.phone),
      house: form.house,
      area: form.area,
      landmark: form.landmark,
      city: form.city,
      state: form.state,
      pincode: Number(form.pincode),
      lat: Number(form.lat),
      lng: Number(form.lng),
      address: fullAddress,
      isDefault: form.isDefault,
    };

    let updated = [...addresses];

    if (newAddress.isDefault) {
      updated = updated.map((a) => ({ ...a, isDefault: false }));
    }

    if (editingIndex !== null) {
      updated[editingIndex] = newAddress;
      setEditingIndex(null);
    } else {
      updated.push(newAddress);
    }

    setAddresses(updated);
    localStorage.setItem("addresses", JSON.stringify(updated));

    // RESET
    setForm({
      name: "",
      phone: "",
      house: "",
      area: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      lat: null,
      lng: null,
      address: "",
      isDefault: false,
    });

    alert("✅ Address Saved");

    // 🔥 AUTO BACK
    setTimeout(() => {
      navigate(-1);
    }, 300);
  };

  // EDIT
  const editAddress = (i) => {
    const a = addresses[i];

    setForm({
      ...a,
      phone: String(a.phone),
      pincode: String(a.pincode),
    });

    setEditingIndex(i);
  };

  // DELETE
  const deleteAddress = (i) => {
    const updated = addresses.filter((_, idx) => idx !== i);
    setAddresses(updated);
    localStorage.setItem("addresses", JSON.stringify(updated));
  };

  // LOAD
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("addresses")) || [];
    setAddresses(saved);
  }, []);

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>📍 Address Manager</h2>

      <button style={styles.locationBtn} onClick={getLocation}>
        {loadingLocation ? "Fetching..." : "📍 Use Current Location"}
      </button>

      <div style={styles.card}>
        <div style={styles.grid}>
          <input style={styles.input} name="name" placeholder="Full Name" value={form.name} onChange={handleChange} />
          <input style={styles.input} name="phone" placeholder="Phone" value={form.phone} onChange={handleChange} />
        </div>

        <input style={styles.input} name="house" placeholder="House / Street" value={form.house} onChange={handleChange} />
        <input style={styles.input} name="area" placeholder="Village / Area" value={form.area} onChange={handleChange} />
        <input style={styles.input} name="landmark" placeholder="Landmark" value={form.landmark} onChange={handleChange} />

        <div style={styles.grid}>
          <input style={styles.input} name="city" placeholder="City" value={form.city} onChange={handleChange} />
          <input style={styles.input} name="state" placeholder="State" value={form.state} onChange={handleChange} />
        </div>

        <input style={styles.input} name="pincode" placeholder="Pincode" value={form.pincode} onChange={handleChange} />

        <label style={styles.checkbox}>
          <input type="checkbox" name="isDefault" checked={form.isDefault} onChange={handleChange} />
          Set as default
        </label>

        <button
          style={{
            ...styles.saveBtn,
            background: form.lat ? "#0a8f3c" : "#ccc",
            cursor: form.lat ? "pointer" : "not-allowed",
          }}
          onClick={saveAddress}
          disabled={!form.lat}
        >
          {!form.lat
            ? "📍 Use Location First"
            : editingIndex !== null
            ? "Update Address"
            : "Save Address"}
        </button>
      </div>

      <div>
        {addresses.map((a, i) => (
          <div key={i} style={styles.addressCard}>
            <div>
              <b>{a.name}</b> ({a.phone})
              <p style={{ margin: "5px 0" }}>{a.address}</p>
              {a.landmark && <p>📍 {a.landmark}</p>}
              {a.isDefault && <span style={styles.defaultTag}>Default</span>}
            </div>

            <div>
              <button style={styles.editBtn} onClick={() => editAddress(i)}>Edit</button>
              <button style={styles.deleteBtn} onClick={() => deleteAddress(i)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// STYLES (same)
const styles = {
  container: { maxWidth: 500, margin: "auto", padding: 15, fontFamily: "sans-serif" },
  heading: { textAlign: "center" },
  locationBtn: { width: "100%", padding: 12, background: "#0a8f3c", color: "#fff", border: "none", borderRadius: 8, marginBottom: 15 },
  card: { background: "#fff", padding: 15, borderRadius: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.1)", marginBottom: 20 },
  input: { width: "100%", padding: 10, marginBottom: 10, borderRadius: 6, border: "1px solid #ccc" },
  grid: { display: "flex", gap: 10 },
  checkbox: { display: "flex", alignItems: "center", gap: 5, marginBottom: 10 },
  saveBtn: { width: "100%", padding: 12, color: "#fff", border: "none", borderRadius: 8 },
  addressCard: { background: "#fff", padding: 12, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: 10, display: "flex", justifyContent: "space-between" },
  editBtn: { marginRight: 5, background: "#ffc107", border: "none", padding: "5px 10px", borderRadius: 5 },
  deleteBtn: { background: "#dc3545", color: "#fff", border: "none", padding: "5px 10px", borderRadius: 5 },
  defaultTag: { background: "#28a745", color: "#fff", padding: "2px 6px", borderRadius: 4, fontSize: 12 },
};

export default AddressManager;